import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import SwipeableLogItem from '../components/SwipeableLogItem'
import LogContent from '../components/LogContent'
import { formatTimestamp } from '../utils/formatters'

/* ───────────────────────────────────────────────────────────────
 *  MemoizedLogItem — the secret weapon
 *
 *  Defined OUTSIDE HomePage so its component identity is stable.
 *  Wraps SwipeableLogItem with React.memo + per-log useCallback for
 *  the inline closures (onDelete / onStar). This is what stops 200
 *  logs from all re-rendering when you star one of them, or when
 *  you type a character in the input bar at the top of the app.
 *
 *  How memo decides:
 *  - If `log` reference is the same as last render → log didn't change
 *  - If handler refs are the same → handlers didn't change
 *  - → skip the render entirely
 *
 *  This only works because HomePage passes us stable handler refs
 *  (see `stable` proxy below) and App.js only creates new log
 *  objects for logs that actually changed.
 * ─────────────────────────────────────────────────────────────── */
const MemoizedLogItem = React.memo(function MemoizedLogItem({
  log, onDeleteConfirm, onToggleStar, onToggleLogStarFull, renderContent, getLogIcon, formatTimestamp,
}) {
  // useCallback gives us stable refs for these per-log closures, so
  // they only change when `log` itself changes. Without this, every
  // parent render would create a new arrow function and break memo.
  const handleDelete = useCallback(
    () => onDeleteConfirm(log),
    [onDeleteConfirm, log]
  )
  const handleStar = useCallback(
    // Swipe-star clears/sets the WHOLE starred state (whole + per-url),
    // matching the card's star indicator. Falls back to the old toggle.
    () => (onToggleLogStarFull ? onToggleLogStarFull(log.id) : onToggleStar(log.id, log.is_starred)),
    [onToggleLogStarFull, onToggleStar, log.id, log.is_starred]
  )

  return (
    <SwipeableLogItem
      log={log}
      onDelete={handleDelete}
      onStar={handleStar}
      renderContent={renderContent}
      getLogIcon={getLogIcon}
      formatTimestamp={formatTimestamp}
    />
  )
})

function HomePage({
  logs, session, homeScrollRef, profilePhotoUrl,
  onDeleteConfirm, onToggleStar, onToggleLogStarFull, onVotePoll, onEditEvent, onEditPoll, onEditCode,
  onImageClick, onGalleryClick, onVideoClick, onDocOpen,
  expandedLogs, setExpandedLogs, getLogIcon,
}) {
  const [showTitle, setShowTitle] = useState(true)

  /* ─── Stable handler proxy ───
   *
   *  App.js redefines its handlers (`toggleStar`, `handleVotePoll`,
   *  etc.) on every render — they're not wrapped in useCallback.
   *  That means their references change every HomePage render too,
   *  which would defeat memo on every child component.
   *
   *  The fix: keep the LATEST handlers in a ref, then build a fixed
   *  proxy object once (empty deps useMemo) that always calls
   *  through the ref. The proxy's references never change for the
   *  lifetime of HomePage, so memo'd children see "props are the
   *  same as last time → skip render".
   *
   *  Trade-off: one extra function call per invocation (negligible).
   */
  const handlersRef = useRef(null)
  handlersRef.current = {
    onDeleteConfirm, onToggleStar, onToggleLogStarFull, onVotePoll, onEditEvent, onEditPoll, onEditCode,
    onImageClick, onGalleryClick, onVideoClick, onDocOpen,
    setExpandedLogs, getLogIcon,
  }

  const stable = useMemo(() => ({
    onDeleteConfirm: (log) => handlersRef.current.onDeleteConfirm(log),
    onToggleStar: (id, cur) => handlersRef.current.onToggleStar(id, cur),
    onToggleLogStarFull: (id) => handlersRef.current.onToggleLogStarFull(id),
    onVotePoll: (id, idx) => handlersRef.current.onVotePoll(id, idx),
    onEditEvent: (log) => handlersRef.current.onEditEvent(log),
    onEditPoll: (log) => handlersRef.current.onEditPoll(log),
    onEditCode: (log) => handlersRef.current.onEditCode(log),
    onImageClick: (data) => handlersRef.current.onImageClick(data),
    onGalleryClick: (data) => handlersRef.current.onGalleryClick(data),
    onVideoClick: (data) => handlersRef.current.onVideoClick(data),
    onDocOpen: (data) => handlersRef.current.onDocOpen(data),
    setExpandedLogs: (...args) => handlersRef.current.setExpandedLogs(...args),
    getLogIcon: (log) => handlersRef.current.getLogIcon(log),
  }), [])

  /* ─── Scroll header fade (unchanged) ─── */
  const handleScroll = useCallback(() => {
    const el = homeScrollRef.current
    if (!el) return
    const scrollTop = el.scrollTop
    const scrollHeight = el.scrollHeight
    const clientHeight = el.clientHeight
    const atTop = scrollTop <= 20
    const atBottom = scrollHeight - scrollTop - clientHeight <= 40
    setShowTitle(atTop || atBottom)
  }, [homeScrollRef])

  useEffect(() => {
    const el = homeScrollRef.current
    if (!el) return
    el.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => el.removeEventListener('scroll', handleScroll)
  }, [homeScrollRef, handleScroll])

  useEffect(() => {
    setTimeout(() => handleScroll(), 100)
  }, [logs, handleScroll])

  /* ─── renderLogContent ───
   *
   *  Wrapped in useCallback so its reference is stable across most
   *  renders. The one dep that DOES change is `expandedLogs` — when
   *  you tap "more" on a log, the Set instance changes, this
   *  callback gets a new ref, and every MemoizedLogItem will see
   *  "renderContent prop changed" and re-render.
   *
   *  This is a known trade-off. Expanding is rare, and the memo
   *  inside MediaRenderer / LinkPreviewCard / DocumentCard means
   *  the expensive children still skip — only the cheap text-layout
   *  JSX in LogContent re-runs. So the cost is small.
   *
   *  To fully eliminate this we'd pass `isExpanded` (boolean) per
   *  log instead of the whole Set. That requires touching LogContent
   *  and LibraryPage too — defer until needed.
   */
  const renderLogContent = useCallback((log) => (
    <LogContent
      log={log}
      session={session}
      profilePhotoUrl={profilePhotoUrl}
      onVotePoll={stable.onVotePoll}
      onEditEvent={stable.onEditEvent}
      onEditPoll={stable.onEditPoll}
      onEditCode={stable.onEditCode}
      onImageClick={stable.onImageClick}
      onGalleryClick={stable.onGalleryClick}
      onVideoClick={stable.onVideoClick}
      onDocOpen={stable.onDocOpen}
      expandedLogs={expandedLogs}
      setExpandedLogs={stable.setExpandedLogs}
    />
  ), [session, profilePhotoUrl, stable, expandedLogs])

  return (
    <div className="app-content no-sb" ref={homeScrollRef}
      style={{ height: '100vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      <div className="home-sticky-header" style={{
        opacity: showTitle ? 1 : 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: showTitle ? 'auto' : 'none',
      }}>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--weight-bold)', margin: 0, color: 'var(--color-text-primary)' }}>Home</h1>
      </div>
      <div className="feed-wrap" style={{ flex: 1 }}>
        {logs.map(log => (
          <MemoizedLogItem
            key={log.id}
            log={log}
            onDeleteConfirm={stable.onDeleteConfirm}
            onToggleStar={stable.onToggleStar}
            onToggleLogStarFull={stable.onToggleLogStarFull}
            renderContent={renderLogContent}
            getLogIcon={stable.getLogIcon}
            formatTimestamp={formatTimestamp}
          />
        ))}
      </div>
    </div>
  )
}

export default HomePage